import { useCallback } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { DATE_ID_FORMAT, formatHoursToTimeString } from '@/helpers';
import { cn } from '@/lib/utils';

import { CELL_GROUP_HEIGHT_PX } from '../../constants';
import { getTimelineCellColorScale } from '../../helpers';
import { calculateCenterTopPosition } from '../../helpers/tooltip-helpers';
import { UtilizationBasedOn } from '../../types/utilization-based-on';
import { usePlannerTooltip } from '../tooltip/planner-tooltip-provider';

export const GroupTimelineCell = ({
  date,
  cellWidth,
  isCompact,
  utilizationPercentage,
  assigned = 0,
  capacity = 0,
  utilizationBasedOn,
  hasNoData = false,
}: {
  date: dayjs.Dayjs;
  cellWidth: number;
  isCompact: boolean;
  utilizationPercentage: number;
  assigned?: number;
  capacity?: number;
  utilizationBasedOn: UtilizationBasedOn;
  /** When true, group-level utilization is unavailable (backfilled members only) */
  hasNoData?: boolean;
}) => {
  const { t } = useTranslation('authenticated.planner');
  const { showTooltip, hideTooltip } = usePlannerTooltip();
  const isToday = date.isSame(dayjs(), 'day');
  const isWeekend = date.day() === 0 || date.day() === 6;

  const color = hasNoData ? undefined : getTimelineCellColorScale(utilizationPercentage);

  const isLastDayOfMonth = date.endOf('month').isSame(date, 'day');

  const tooltipContent = hasNoData
    ? t('userInfo.tooltip.utilizationCell.noData')
    : t(
        `userInfo.tooltip.utilizationCell.${utilizationBasedOn === UtilizationBasedOn.BillableCapacity ? 'billable' : 'total'}`,
        {
          utilizationPercent: utilizationPercentage.toFixed(0),
          assigned: formatHoursToTimeString(assigned / 60),
          capacity: formatHoursToTimeString(capacity / 60),
        }
      );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isWeekend) {
        const { centerX, topY } = calculateCenterTopPosition(e.currentTarget);
        showTooltip(tooltipContent, centerX, topY);
      }
    },
    [isWeekend, tooltipContent, showTooltip]
  );

  const handleMouseLeave = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  return (
    <div
      className={cn('relative overflow-hidden border-b border-r', {
        'pointer-events-none bg-muted/50': isWeekend,
        'border-r-2': isLastDayOfMonth,
        'bg-success/10 dark:bg-success/20': isToday,
      })}
      id={`cell-${date.format(DATE_ID_FORMAT)}`}
      style={{
        width: `${cellWidth}rem`,
        minWidth: `${cellWidth}rem`,
        height: CELL_GROUP_HEIGHT_PX,
      }}
    >
      {!isWeekend &&
        (hasNoData ? (
          <div
            className="flex h-full w-full items-center justify-center py-1 text-center text-[10px] text-muted-foreground"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            -
          </div>
        ) : isCompact ? (
          <div
            className="flex h-full w-full items-center justify-center pt-2"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="h-2 w-2 rounded-full" style={{ background: color }} />
          </div>
        ) : (
          <div
            className="flex h-full w-full items-center justify-center py-1 text-center text-[10px]"
            style={{ color }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {t('userInfo.percentage', {
              value: utilizationPercentage.toFixed(0),
            })}
          </div>
        ))}
    </div>
  );
};
